import { makeStyles } from "tss-react/mui"
import { faImage } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Grid, IconButton, Typography, Stack } from "@mui/material"
import { StandardChip } from "../../../../../shared/components/badges/StandardChip"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { DefaultPreviewProductImageStateType } from "./PreviewProductImage"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import { toDinero } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"
import { DishItem } from "../menuPlanningTypes"

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: "1rem",
        marginLeft: "5.25rem",
        borderLeft: `2px solid ${tailwindPalette.slate[200]}`,
        display: "grid",
        gap: "1.5rem",
        color: tailwindPalette.slate[600],
    },
    spacing: {
        gap: "0.625rem",
    },
    title: {
        color: theme.palette.common.black,
        fontWeight: 500,
    },
    textColor: {
        color: theme.palette.text.primary,
    },
}))

const formatAllergens = (dish: DishItem) => dish.productDescription?.allergens?.map((el) => el.name).join(", ") || "-"

type DetailedMenuProps = Readonly<{
    dishes: DishItem[]
    onProductPhotoClick: React.Dispatch<React.SetStateAction<DefaultPreviewProductImageStateType>>
}>

export function DetailedMenu({ dishes, onProductPhotoClick }: DetailedMenuProps) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})

    return (
        <Box className={classes.container}>
            {dishes.map((dish: DishItem, index: number) => (
                <Stack direction="column" key={index}>
                    <LocalizedStrict id="merchant-menu-planning-table-dish" vars={{ counter: index + 1 }}>
                        <Typography variant="body2" className={classes.title}>
                            Dish :
                        </Typography>
                    </LocalizedStrict>
                    <Grid container flexWrap="nowrap" alignItems="center" className={classes.spacing}>
                        <Typography variant="body2" className={classes.textColor}>
                            {dish.name}
                        </Typography>
                        {dish?.productPhoto?.url && (
                            <Grid item>
                                <IconButton
                                    onClick={() =>
                                        onProductPhotoClick({ show: true, image: dish?.productPhoto?.url || "" })
                                    }
                                >
                                    <FontAwesomeIcon icon={faImage} fontSize="16px" color="#94A3B8" />
                                </IconButton>
                            </Grid>
                        )}
                        {dish.productDescription?.marketingTag && (
                            <Grid item>
                                <StandardChip
                                    size="small"
                                    label={dish.productDescription?.marketingTag}
                                    variant="yellow"
                                />
                            </Grid>
                        )}
                    </Grid>
                    <Typography variant="body2">
                        <LocalizedStrict
                            id="merchant-menu-planning-table-price"
                            vars={{ price: dish?.price ? moneyFactory.format(toDinero(dish.price)) : "-" }}
                        >
                            <>Price {dish?.price}</>
                        </LocalizedStrict>
                    </Typography>
                    <Typography variant="body2">
                        <LocalizedStrict
                            id="merchant-menu-planning-table-allergens"
                            vars={{ allergens: formatAllergens(dish) }}
                        >
                            <>Allergens: {formatAllergens(dish)}</>
                        </LocalizedStrict>
                    </Typography>
                </Stack>
            ))}
        </Box>
    )
}
